import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";

import type { CrmDataProvider } from "../../providers/types";
import type {
  InboxConversation,
  InboxState,
  InboxTransitionInput,
  InboxTransitionResult,
} from "../../types";
import { releaseHolder } from "./felixOwnership";
import { mergeInboxPages, sortInboxRows, summaryCount } from "./inboxModel";

/** Prefix shared by every inbox query so one invalidation refreshes the nav
 *  badge, the list tabs and the per-contact attention state together. */
export const INBOX_QUERY_KEY = ["whatsapp-inbox"] as const;

/** Same key as `contacts/cohortFilters.ts` so the `/health` capability probe is
 *  fetched once and shared across the header, the inbox and the filters. */
export const DASHBOARD_CAPABILITIES_QUERY_KEY = [
  "dashboardCapabilities",
] as const;

export const CONTACT_CONVERSATION_QUERY_PREFIX = "contact-conversation";

export function conversationQueryKey(contactId: Identifier) {
  return [CONTACT_CONVERSATION_QUERY_PREFIX, String(contactId)] as const;
}

export function inboxErrorMessage(
  error: unknown,
  fallback = "crm.whatsapp.inbox.error",
): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function useDashboardCapabilities() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  return useQuery({
    queryKey: DASHBOARD_CAPABILITIES_QUERY_KEY,
    queryFn: () => dataProvider.getDashboardCapabilities(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/** Whether the connected dashboard API advertises `features.whatsapp_inbox`. */
export function useInboxCapability(): {
  enabled: boolean;
  resolved: boolean;
  error: string | null;
} {
  const query = useDashboardCapabilities();
  return {
    enabled: query.data?.features?.whatsapp_inbox === true,
    resolved: query.isSuccess || query.isError,
    error: query.isError ? inboxErrorMessage(query.error) : null,
  };
}

export function useInboxEnabled(): boolean {
  return useInboxCapability().enabled;
}

export function useInboxSummary() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const enabled = useInboxEnabled();
  return useQuery({
    queryKey: [...INBOX_QUERY_KEY, "summary"],
    queryFn: () => dataProvider.getInboxSummary(),
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: 1,
  });
}

/** Needs-reply count for the header pill; `null` hides the badge (feature
 *  absent, still loading, or the summary failed — never break the header). */
export function useInboxBadgeCount(): number | null {
  const enabled = useInboxEnabled();
  const { data } = useInboxSummary();
  if (!enabled || !data) return null;
  return summaryCount(data, "needs_reply");
}

export function useInboxConversations(
  state: InboxState,
  { limit = 50 }: { limit?: number } = {},
) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const enabled = useInboxEnabled();
  const query = useInfiniteQuery({
    queryKey: [...INBOX_QUERY_KEY, "list", state, limit],
    queryFn: ({ pageParam }) =>
      dataProvider.listInboxConversations({ state, cursor: pageParam, limit }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.has_more && last.next_cursor ? last.next_cursor : undefined,
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const rows = useMemo(() => {
    const merged = mergeInboxPages(query.data?.pages ?? []);
    // Resolved rows keep the server's recency order; open episodes are triaged
    // by priority then waiting time.
    return state === "resolved" ? merged : sortInboxRows(merged);
  }, [query.data, state]);
  const total = query.data?.pages[0]?.total ?? null;
  return { ...query, rows, total };
}

export function useInboxConversation(contactId: Identifier | null | undefined) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const enabled = useInboxEnabled() && contactId != null;
  return useQuery({
    queryKey: [...INBOX_QUERY_KEY, "conversation", String(contactId ?? "")],
    queryFn: () => dataProvider.getInboxConversation(contactId as Identifier),
    enabled,
    staleTime: 10_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    retry: 1,
  });
}

export type InboxTransitionAction = "claim" | "release" | "resolve" | "reopen";

export type InboxTransitionVariables = {
  action: InboxTransitionAction;
  contactId: Identifier;
  note?: string;
  /** Release only: end a teammate's claim after the operator confirmed. */
  force?: boolean;
};

export function invalidateInboxQueries(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY });
}

/** Claim / resolve / reopen with notifications and cache invalidation. */
export function useInboxTransition() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const operatorName = identity?.fullName ?? undefined;

  const mutation = useMutation({
    mutationFn: async ({
      action,
      contactId,
      note,
      force,
    }: InboxTransitionVariables): Promise<InboxTransitionResult> => {
      const input: InboxTransitionInput = { note, operator_name: operatorName };
      if (action === "claim") {
        return dataProvider.claimInboxConversation(contactId, input);
      }
      if (action === "release") {
        return dataProvider.releaseInboxConversation(contactId, {
          ...input,
          force: force === true,
        });
      }
      if (action === "resolve") {
        return dataProvider.resolveInboxConversation(contactId, input);
      }
      return dataProvider.reopenInboxConversation(contactId, input);
    },
    onSuccess: async (result, variables) => {
      queryClient.setQueryData(
        [...INBOX_QUERY_KEY, "conversation", String(variables.contactId)],
        result.data,
      );
      notify(`crm.whatsapp.inbox.actions.${variables.action}_done`, {
        type: "success",
      });
      await invalidateInboxQueries(queryClient);
    },
    onError: (error) => {
      notify(inboxErrorMessage(error), { type: "error" });
    },
  });

  const { mutateAsync, isPending, variables } = mutation;

  /** Runs the transition; resolves to `null` on failure (already notified). */
  const run = useCallback(
    (input: InboxTransitionVariables) =>
      mutateAsync(input).catch(() => null as InboxTransitionResult | null),
    [mutateAsync],
  );

  const isPendingFor = useCallback(
    (contactId?: Identifier, action?: InboxTransitionAction) =>
      isPending &&
      (contactId == null ||
        String(variables?.contactId) === String(contactId)) &&
      (action == null || variables?.action === action),
    [isPending, variables],
  );

  /**
   * Hands the conversation back to Felix (FHC-011). Releasing a teammate's
   * claim asks first, then sends `force`; resolves to `null` when declined.
   */
  const release = useCallback(
    (row: InboxConversation) => {
      const holder = releaseHolder(row, operatorName);
      if (holder) {
        const ok = window.confirm(
          translate("crm.whatsapp.inbox.actions.release_confirm", {
            name: holder,
          }),
        );
        if (!ok) return Promise.resolve(null as InboxTransitionResult | null);
      }
      return run({
        action: "release",
        contactId: row.contact_id,
        force: holder != null,
      });
    },
    [operatorName, run, translate],
  );

  return { run, release, isPending, isPendingFor };
}

/** Ticks every `intervalMs` so relative times and countdowns stay current. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}
